/**
 * Motion utilities - SIMPLIFIED VERSION (No animations due to deployment issues)
 * Returns plain React components without Motion library to avoid MessagePort errors
 */

import React from 'react';

// Filter out motion-specific props that shouldn't be passed to DOM elements
const filterMotionProps = (props: any) => {
  const {
    variants,
    initial,
    animate,
    exit,
    transition,
    layoutId,
    whileHover,
    whileTap,
    whileFocus,
    whileDrag,
    drag,
    dragConstraints,
    dragElastic,
    dragMomentum,
    onDragStart,
    onDragEnd,
    onDrag,
    ...filteredProps
  } = props;
  return filteredProps;
};

// Mock motion object that returns plain divs
export const motion = {
  div: React.forwardRef<HTMLDivElement, any>(({ children, className, style, onClick, ...props }, ref) => (
    <div ref={ref} className={className} style={style} onClick={onClick} {...filterMotionProps(props)}>
      {children}
    </div>
  )),
  span: React.forwardRef<HTMLSpanElement, any>(({ children, className, style, ...props }, ref) => (
    <span ref={ref} className={className} style={style} {...filterMotionProps(props)}>
      {children}
    </span>
  )),
  button: React.forwardRef<HTMLButtonElement, any>(({ children, className, style, onClick, ...props }, ref) => (
    <button ref={ref} className={className} style={style} onClick={onClick} {...filterMotionProps(props)}>
      {children}
    </button>
  )),
  header: React.forwardRef<HTMLElement, any>(({ children, className, style, ...props }, ref) => (
    <header ref={ref as any} className={className} style={style} {...filterMotionProps(props)}>
      {children}
    </header>
  )),
  nav: React.forwardRef<HTMLElement, any>(({ children, className, style, role, 'aria-label': ariaLabel, ...props }, ref) => (
    <nav ref={ref as any} className={className} style={style} role={role} aria-label={ariaLabel} {...filterMotionProps(props)}>
      {children}
    </nav>
  )),
};

// Add display names for better debugging
if (motion.div) {
  (motion.div as any).displayName = 'motion.div';
}
if (motion.span) {
  (motion.span as any).displayName = 'motion.span';
}
if (motion.button) {
  (motion.button as any).displayName = 'motion.button';
}

// Mock AnimatePresence that just renders children (ignores framer-motion-only props like `mode`)
export const AnimatePresence = ({
  children,
  mode: _mode,
}: {
  children: React.ReactNode;
  mode?: string;
}) => <>{children}</>;

// Animation variants (kept for compatibility but not used)
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
};

export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const slideUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const slideInFromBottom = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 30 }
};

export const slideInFromTop = {
  initial: { opacity: 0, y: -30 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 }
};

export const slideInRight = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 }
};

export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const staggerItem = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 }
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 }
};

// Backdrop component
export const Backdrop = ({ children, onClick }: { children?: React.ReactNode; onClick?: () => void }) => {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 40
      }}
    >
      {children}
    </div>
  );
};

// Motion div component
export const MotionDiv = React.forwardRef<HTMLDivElement, any>(({ children, className, style, onClick, ...props }, ref) => (
  <div ref={ref} className={className} style={style} onClick={onClick} {...filterMotionProps(props)}>
    {children}
  </div>
));
MotionDiv.displayName = 'MotionDiv';

// Animated number component
export const AnimatedNumber = ({ value }: { value: number }) => {
  return <span>{value}</span>;
};

// Page transition component
export const PageTransition = ({ children }: { children: React.ReactNode }) => {
  return <div className="animate-fadeIn">{children}</div>;
};